/**
 * Employee Niveau Override React Query Hooks
 *
 * Epic 1 — Roltypes & niveau-bepaling v1, Story 1.3
 * (Handmatige niveau-override met reden).
 *
 * Custom hooks for reading niveau override status and setting/removing a
 * manual niveau override on an employee.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { niveauOverrideApi, type SetNiveauOverrideInput } from '../../lib/employeesApi';
import { employeeKeys } from './useEmployees';

// Query keys for niveau override status
export const niveauOverrideKeys = {
  status: (employeeId: number) => [...employeeKeys.detail(employeeId), 'niveauStatus'] as const,
};

/**
 * Hook to fetch the niveau status (current, calculated, override info) for
 * an employee.
 *
 * `enabled` (default true) lets callers gate the request by role — the
 * backend route is `requireRole(['admin','hr'])`, so calling this
 * unconditionally for a `beoordelaar`/`medewerker` viewer would fire a
 * guaranteed-403 request on every employee-detail page load (code-review
 * fix — see Story 1.3 review notes).
 */
export function useNiveauStatus(employeeId: number | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: niveauOverrideKeys.status(employeeId!),
    queryFn: () => niveauOverrideApi.getNiveauStatus(employeeId!),
    enabled: !!employeeId && enabled,
  });
}

/**
 * Hook to set a manual niveau override on an employee (AC1, AC7).
 */
export function useSetNiveauOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, input }: { employeeId: number; input: SetNiveauOverrideInput }) =>
      niveauOverrideApi.setNiveauOverride(employeeId, input),
    onSuccess: (_, { employeeId }) => {
      // AC7: directe UI-update zonder refresh.
      queryClient.invalidateQueries({ queryKey: niveauOverrideKeys.status(employeeId) });
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(employeeId) });
    },
  });
}

/**
 * Hook to remove a manual niveau override on an employee (AC1, AC7).
 */
export function useRemoveNiveauOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (employeeId: number) => niveauOverrideApi.removeNiveauOverride(employeeId),
    onSuccess: (_, employeeId) => {
      queryClient.invalidateQueries({ queryKey: niveauOverrideKeys.status(employeeId) });
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(employeeId) });
    },
  });
}
